// Training guide, in the app.
//
// Why in-app rather than a document (product owner, 2026-07-25): the standalone HTML
// guide "didn't work great — clunky for people to open, boxes were too small,
// and 'open in app' was awkward". Living here means three things a document
// can't do: it only shows what you can actually reach, it links to the real
// screen instead of a screenshot that ages, and it's one click from the work.
//
// Content comes from content/ — never written inline here, so the Word export
// renders the same words.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { usersApi, manpowerApi, trainingApi } from '@/lib/apiClient';
import { TRAINING } from './content';
import type { Section, Topic, Track } from './content/types';
import { viewerFrom, visibleSections, allows, isFieldLeaderOnly, suggestedTrack } from './lib/access';
import { targetTopics } from './lib/target';
import { downloadTrainingDocx } from './lib/docx';
import { entryMode, nextGuideParams, readMode, rememberMode, type GuideAction, type GuideEntry } from './lib/mode';
import TakeMeThere, { releaseAppTab, takeMeThereCss } from './TakeMeThere';
import TopicProgress from './TopicProgress';
import StartHere from './StartHere';
import GuidedMode from './GuidedMode';

const css = `
  .tg-wrap { max-width: 1100px; margin: 0 auto; padding: var(--ew-s-5) var(--ew-s-6) var(--ew-s-8); }
  .tg-head h1 { font: var(--ew-fw-bold) 26px/1.2 var(--ew-font); color: var(--ew-dark); margin: 0 0 4px; }
  .tg-sub { font: var(--ew-fw-regular) 13.5px/1.5 var(--ew-font); color: var(--ew-n-500); margin: 0; }
  .tg-bar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin: var(--ew-s-5) 0 var(--ew-s-4); }
  .tg-pill {
    border: 1px solid var(--ew-n-200); background: #fff; color: var(--ew-n-600); cursor: pointer;
    border-radius: var(--ew-r-pill); padding: 6px 14px; font: var(--ew-fw-medium) 12.5px/1 var(--ew-font);
  }
  .tg-pill.active { background: var(--ew-dark); border-color: var(--ew-dark); color: #fff; }
  .tg-pill .tg-you { font-size: 10px; opacity: .75; margin-left: 5px; text-transform: uppercase; letter-spacing: .05em; }
  .tg-search {
    margin-left: auto; min-width: 220px; font: inherit; font-size: 13px; padding: 7px 10px;
    border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); background: #fff; font-family: var(--ew-font);
  }
  .tg-sec { margin-bottom: var(--ew-s-6); }
  /* ── Heading hierarchy ──────────────────────────────────────────────────────
     One class used to render all three levels (part title, track title, section
     heading), separated only by a 1px font-size override. Since the topic cards
     inside a section are 15px near-black, a 12px grey section heading was LIGHTER
     than the things it grouped — inverted hierarchy, so the eye read the cards as
     the structure and skipped the headings entirely (product owner, 2026-07-29: "easy to
     miss some of the headings because of the gray text").

     Note the grey itself was never the problem: --ew-n-500 on white is about
     6:1, comfortably past AA. It was the RELATIVE weight, which is why darkening
     alone wouldn't have fixed it. Each level now differs in size, weight, colour
     and spacing, and sections carry a rule so a new block is visible while
     scrolling. */

  /* Level 1 — "Everyone starts here" / the track name. A title, not a label, so
     no uppercase: sentence case at size reads faster. */
  .tg-mod-loc { font: var(--ew-fw-medium) 11.5px/1.4 var(--ew-font); color: var(--ew-n-500); margin: 0 0 6px; }
  .tg-part-h { font: var(--ew-fw-bold) 20px/1.25 var(--ew-font); color: var(--ew-dark); margin: 0 0 2px; }
  /* Adjacent-sibling spacing rather than :first-child — reliable regardless of
     what else the page renders above. */
  .tg-part + .tg-part { margin-top: 34px; }

  /* Level 2 — section heading. Keeps the house small-caps treatment (it matches
     card headers elsewhere in the app) but at full contrast and bold, with a rule
     above so it reads as a break rather than a caption on the card below. */
  /* 14px, not the 12.5px an eyebrow label would take: the topic titles inside a
     section are 15px, so anything much smaller loses the size contest with the
     content it's meant to be labelling — which is the actual reason a heading
     gets skipped. Bold + uppercase + tracking at 14px now outranks 15px semibold
     sentence case without the page starting to shout. */
  .tg-sec-h { font: var(--ew-fw-bold) 14px/1.25 var(--ew-font); letter-spacing: .07em;
              text-transform: uppercase; color: var(--ew-dark);
              border-top: 1px solid var(--ew-n-200); padding-top: 18px; margin: 28px 0 8px; }
  /* The first section under a part title already has the title as its separator.
     Scoped inside .tg-part, where the .tg-sec divs are the only divs, so
     :first-of-type is unambiguous. */
  .tg-part > .tg-sec:first-of-type > .tg-sec-h { border-top: 0; padding-top: 0; margin-top: 14px; }
  .tg-sec-intro { font: var(--ew-fw-regular) 13px/1.5 var(--ew-font); color: var(--ew-n-600); margin: 0 0 12px; }
  .tg-topic { background: #fff; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md);
              padding: 14px 16px; margin-bottom: 10px; scroll-margin-top: 90px; }
  .tg-topic.hit { border-color: var(--ew-quality); box-shadow: 0 0 0 3px rgba(21,101,158,.10); }
  .tg-topic h3 { font: var(--ew-fw-semibold) 15px/1.35 var(--ew-font); color: var(--ew-dark); margin: 0 0 6px; }
  .tg-summary { font: var(--ew-fw-regular) 13.5px/1.6 var(--ew-font); color: var(--ew-n-700); margin: 0 0 10px; }
  .tg-owner { display: inline-block; font: var(--ew-fw-semibold) 10px/1.6 var(--ew-font); letter-spacing: .04em;
              text-transform: uppercase; background: var(--ew-n-50); border: 1px solid var(--ew-n-200);
              color: var(--ew-n-600); border-radius: var(--ew-r-pill); padding: 1px 9px; margin-left: 8px;
              vertical-align: middle; }
  .tg-steps { margin: 0 0 10px; padding-left: 20px; }
  .tg-steps li { font: var(--ew-fw-regular) 13.5px/1.7 var(--ew-font); color: var(--ew-n-700); }
  .tg-watch { background: #FFFDF5; border: 1px solid #E7D9A6; border-radius: var(--ew-r-sm);
              padding: 9px 12px; margin: 0 0 10px; }
  .tg-watch ul { margin: 0; padding-left: 18px; }
  .tg-watch li { font: var(--ew-fw-regular) 12.5px/1.6 var(--ew-font); color: #6B5D1F; }
  .tg-tbl { width: 100%; border-collapse: collapse; font-size: 12.5px; margin: 0 0 10px; }
  .tg-tbl caption { text-align: left; font: var(--ew-fw-semibold) 11px/1.6 var(--ew-font);
                    letter-spacing: .05em; text-transform: uppercase; color: var(--ew-n-500); padding-bottom: 5px; }
  .tg-tbl th { text-align: left; padding: 7px 10px; background: var(--ew-n-50); color: var(--ew-n-600);
               font: var(--ew-fw-semibold) 11px/1.4 var(--ew-font); border-bottom: 1px solid var(--ew-n-200); }
  .tg-tbl td { padding: 7px 10px; border-bottom: 1px solid #F2F2F1; color: var(--ew-dark); vertical-align: top; line-height: 1.5; }
  .tg-go { font: var(--ew-fw-semibold) 12.5px/1 var(--ew-font); color: var(--ew-quality); text-decoration: underline; }
  .tg-where { font: var(--ew-fw-regular) 12.5px/1.5 var(--ew-font); color: var(--ew-n-500); }
  .tg-empty { background: var(--ew-n-50); border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md);
              padding: 16px; font-size: 13px; color: var(--ew-n-600); }
  /* Phase 3 — the tick, the note, and the staleness marker. Kept visually quiet:
     it sits on every card, so anything louder would compete with the content. */
  .tp-row { display: flex; flex-wrap: wrap; align-items: center; gap: 10px;
            border-top: 1px solid #F2F2F1; margin-top: 12px; padding-top: 10px; }
  .tp-tick { display: inline-flex; align-items: center; gap: 6px; cursor: pointer;
             font: var(--ew-fw-medium) 12.5px/1 var(--ew-font); color: var(--ew-n-600); }
  .tp-tick input { width: 15px; height: 15px; accent-color: var(--ew-green); cursor: pointer; }
  .tp-stale { font: var(--ew-fw-semibold) 10.5px/1.6 var(--ew-font); letter-spacing: .03em;
              background: #FFFDF5; border: 1px solid #E7D9A6; color: #6B5D1F;
              border-radius: var(--ew-r-pill); padding: 1px 9px; cursor: help; }
  .tp-note-btn { border: none; background: none; padding: 0; cursor: pointer; margin-left: auto;
                 font: var(--ew-fw-regular) 12px/1 var(--ew-font); color: var(--ew-quality);
                 border-bottom: 1px dotted currentColor; }
  .tp-note { width: 100%; }
  .tp-note textarea { width: 100%; font: var(--ew-fw-regular) 13px/1.5 var(--ew-font); padding: 8px 10px;
                      border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); resize: vertical;
                      background: #fff; color: var(--ew-dark); }
  .tp-note-actions { display: flex; align-items: center; gap: 8px; margin-top: 8px; }
  .tp-vis { margin-right: auto; font: var(--ew-fw-regular) 11px/1.4 var(--ew-font); color: var(--ew-n-500); }
  .tp-btn { font: var(--ew-fw-semibold) 12px/1 var(--ew-font); border-radius: var(--ew-r-md); padding: 7px 12px;
            cursor: pointer; border: 1px solid var(--ew-n-300); background: #fff; color: var(--ew-dark); }
  .tp-btn.primary { background: var(--ew-green); border-color: var(--ew-green); color: #fff; }
  .tp-btn:disabled { opacity: .5; cursor: not-allowed; }
  .tp-note-read { width: 100%; margin: 4px 0 0; font: var(--ew-fw-regular) 12.5px/1.5 var(--ew-font);
                  color: var(--ew-n-600); background: var(--ew-n-50); border-left: 2px solid var(--ew-n-300);
                  padding: 6px 10px; white-space: pre-wrap; }
  .tp-progress { font: var(--ew-fw-medium) 12px/1 var(--ew-font); color: var(--ew-n-500); margin-left: 8px; }
  .tg-scope { background: #E7F0F8; border: 1px solid #CFE0EF; border-radius: var(--ew-r-md);
              padding: 9px 12px; margin-bottom: var(--ew-s-4); font-size: 12.5px; line-height: 1.5; color: #15659E; }
${takeMeThereCss}
`;

/** Minimal inline formatting: **bold** only. The content uses it for emphasis
 *  inside sentences, and pulling in a markdown library for one rule would be
 *  more surface than it's worth. */
function RichText({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith('**') && p.endsWith('**')
          ? <strong key={i}>{p.slice(2, -2)}</strong>
          : <span key={i}>{p}</span>,
      )}
    </>
  );
}

function TopicCard({ topic, highlight, progress, reference = false }: {
  topic: Topic;
  highlight: boolean;
  progress?: import('@/lib/apiClient').TrainingProgress;
  /** Appendix cards: notes, but no completion tick — see TopicProgress. */
  reference?: boolean;
}) {
  return (
    <div className={`tg-topic${highlight ? ' hit' : ''}`} id={`topic-${topic.id}`}>
      <h3>
        {topic.title}
        {topic.owner && <span className="tg-owner" title="Who owns this work at E&amp;W">Owned by {topic.owner}</span>}
      </h3>
      <p className="tg-summary"><RichText text={topic.summary} /></p>

      {topic.steps && topic.steps.length > 0 && (
        <ol className="tg-steps">
          {topic.steps.map((s, i) => <li key={i}><RichText text={s} /></li>)}
        </ol>
      )}

      {topic.table && (
        <table className="tg-tbl">
          {topic.table.caption && <caption>{topic.table.caption}</caption>}
          <thead>
            <tr>{topic.table.headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {topic.table.rows.map((r, i) => (
              <tr key={i}>{r.map((c, j) => <td key={j}><RichText text={c} /></td>)}</tr>
            ))}
          </tbody>
        </table>
      )}

      {topic.watchOut && topic.watchOut.length > 0 && (
        <div className="tg-watch">
          <ul>{topic.watchOut.map((w, i) => <li key={i}><RichText text={w} /></li>)}</ul>
        </div>
      )}

      {/* A link to the live screen replaces the screenshot — it can't go stale.
          It opens alongside the guide rather than over it; see TakeMeThere. */}
      {topic.deepLink && <TakeMeThere href={topic.deepLink} linkClass="tg-go" />}
      {!topic.deepLink && topic.deepLinkNote && (
        <div className="tg-where">Where to find it: {topic.deepLinkNote}</div>
      )}

      <TopicProgress topicId={topic.id} revision={TRAINING.revision} progress={progress} reference={reference} />
    </div>
  );
}

const matches = (t: Topic, q: string) => {
  if (!q) return false;
  const hay = [t.title, t.summary, ...(t.steps ?? []), ...(t.watchOut ?? [])].join(' ').toLowerCase();
  return hay.includes(q.toLowerCase());
};

export default function TrainingPage() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  /** The .docx is assembled in the browser and can take a moment on a big
   *  guide, so the button reports itself rather than appearing to do nothing. */
  const [exporting, setExporting] = useState(false);
  /** What the reader has picked THIS visit; null until they touch anything, at
   *  which point `entryMode` no longer gets a say. */
  const [chosen, setChosen] = useState<GuideEntry | null>(null);
  /** Read once. Re-reading on every render would let a write in this tab race
   *  the state we already hold. */
  const storedMode = useRef(readMode());

  /** ⚠️ Whichever tab the guide is being read in is not the tracker tab — see
   *  releaseAppTab. Without this, arriving here from the tracker tab (the `?`
   *  Help drawer links to /training from every page) makes every "Take me
   *  there" below target the tab it is already in. */
  useEffect(() => { releaseAppTab(); }, []);

  // The reader's own ticks and notes. Keyed by topic id; rows for topics that no
  // longer exist are simply never looked up, so deleting a topic is harmless.
  const progressQuery = useQuery({ queryKey: ['training-progress'], queryFn: trainingApi.myProgress, staleTime: 30_000 });
  const progressBy = useMemo(() => {
    const m = new Map<string, import('@/lib/apiClient').TrainingProgress>();
    for (const p of progressQuery.data ?? []) m.set(p.topicId, p);
    return m;
  }, [progressQuery.data]);

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60_000 });
  // Financials/Commitments content is gated on the same admin toggle the tabs are.
  const { data: config } = useQuery({ queryKey: ['manpower', 'config'], queryFn: manpowerApi.config, staleTime: 60_000 });
  const reader = useMemo(
    () => viewerFrom(me, config?.showProjectCosts === '1'),
    [me, config],
  );

  // Every track stays readable — a PE may well want to read the PM track, and a
  // new PM benefits from seeing what the field leader is being asked to do — but
  // the one matching the reader is preselected, and its CONTENTS are still
  // access-filtered, so reading another track never teaches you a button you
  // don't have.
  // Tracks the reader may OPEN. Nearly all are open to everyone — reading
  // another role's guide is usually useful — but a track carrying its own
  // `access` rule is filtered out here, which removes it from the picker AND,
  // because `active` is resolved from this list, makes a hand-typed
  // `?track=admin` fall back rather than open. The Administrator track is the
  // only one gated this way.
  const tracks = useMemo(
    () => TRAINING.tracks.filter((t) => allows(reader, t.access)),
    [reader],
  );
  // The most specific match, not the first — see suggestedTrack. Note this runs
  // over `tracks` (what the reader may open), so a non-admin can never be
  // suggested the admin track even if the tier rule were wrong.
  const suggested: Track | undefined = useMemo(
    () => suggestedTrack(reader, tracks),
    [tracks, reader],
  );

  const activeId = params.get('track') ?? suggested?.id ?? 'pm';
  const active = tracks.find((t) => t.id === activeId) ?? tracks[0];

  // The Field Leader track is scoped by TRACK, not by reader (product owner, 2026-07-28):
  // they're the only role confined to their assigned jobs, so that track must be
  // wholly about what they can see and must never mention a company-wide screen.
  // Forcing the scope means an admin previewing it reads the real thing.
  const viewer = useMemo(
    () => (active?.id === 'field-leader' ? { ...reader, forceFieldLeaderScope: true } : reader),
    [reader, active],
  );
  const foundations: Section[] = useMemo(
    () => visibleSections(viewer, TRAINING.foundations),
    [viewer],
  );
  const trackSections: Section[] = useMemo(
    () => (active ? visibleSections(viewer, active.sections) : []),
    [viewer, active],
  );

  const focusTopic = params.get('topic');
  const q = query.trim();

  // Which face of the guide this is. `undefined` means "not decided yet": with
  // no remembered choice the answer depends on whether they've ticked anything
  // before, so it has to wait for that one query rather than flash a role picker
  // at someone who is half way through the guide.
  const forceStart = params.get('start') === '1';
  /** `?guided=<topicId>` — a shared link that opens the walkthrough AT one
   *  topic, rather than at the first unread one, which would otherwise skip
   *  past it if the reader had already ticked it. */
  const guidedAt = params.get('guided');
  const entry: GuideEntry | undefined = useMemo(() => {
    if (chosen) return chosen;
    if (forceStart) return 'start';
    if (guidedAt) return 'guided';
    if (!focusTopic && !storedMode.current && progressQuery.isPending) return undefined;
    return entryMode({
      deepLinkTopic: focusTopic,
      stored: storedMode.current,
      hasProgress: progressBy.size > 0,
    });
  }, [chosen, forceStart, guidedAt, focusTopic, progressQuery.isPending, progressBy]);

  /**
   * Bring a `?topic=` card into view.
   *
   * ⚠️ The highlight alone was not a return. `?topic=` is not a URL fragment, so
   * the browser scrolls nowhere — on a guide this long the reader landed at the
   * top with the card they asked for somewhere below the fold (review, #289).
   * The `scroll-margin-top` on `.tg-topic` shows this was always the intent; it
   * only ever had an effect for something that scrolled, and nothing did.
   *
   * Runs after paint, so the card exists — but only in reference mode, where it
   * is rendered at all. Search or a track change can filter it away, hence the
   * null check rather than an assumption.
   */
  useEffect(() => {
    if (entry !== 'reference' || !focusTopic) return;
    const el = document.getElementById(`topic-${focusTopic}`);
    if (!el) return;
    // Honour a reduced-motion preference; a long smooth scroll is exactly the
    // kind of movement that setting exists to suppress.
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  }, [entry, focusTopic, q, active?.id]);

  /**
   * ⚠️ ONE URL UPDATE PER ACTION, and what it becomes is decided by
   * `nextGuideParams`, not here. Two updates in one handler both build from
   * `params` as captured by the render still on screen, so the second silently
   * reverts the first — that's what `setTrack(id)` then `pickMode('guided')` did
   * from the role picker, discarding the role just chosen (review, #289).
   */
  const apply = (a: GuideAction) => setParams(nextGuideParams(params, a), { replace: true });

  const setTrack = (id: string) => apply({ kind: 'track', id });

  /** Choosing is remembered; a deep link never overwrites it. */
  const pickMode = (m: 'guided' | 'reference') => {
    rememberMode(m);
    setChosen(m);
    apply({ kind: 'mode' });
  };

  /** A card on the "Start here" screen: the track AND the mode, in one update. */
  const pickRole = (id: string) => {
    rememberMode('guided');
    setChosen('guided');
    apply({ kind: 'role', id });
  };

  /** Back to the role picker, via the URL rather than component state, so the
   *  screen survives a refresh and can be linked to ("/training?start=1"). */
  const backToStart = () => {
    setChosen(null);
    apply({ kind: 'start' });
  };

  // Searching spans foundations AND the open track, because people search for a
  // word without knowing which half of the guide it lives in.
  const filtered = (secs: Section[]) =>
    q ? secs.map((s) => ({ ...s, topics: s.topics.filter((t) => matches(t, q)) })).filter((s) => s.topics.length) : secs;

  // Modules the reader can open, with the same search applied. Topic-level
  // rules still run inside visibleSections, so a module can survive while one
  // of its topics is dropped.
  const shownModules = useMemo(() => {
    const open = TRAINING.modules.filter((m) => allows(viewer, m.access));
    if (!q) return open.map((m) => ({ ...m, topics: m.topics.filter((t) => allows(viewer, t.access)) }));
    return open
      .map((m) => {
        // A module's own name, location and purpose are searchable, not just
        // its topics. Searching "Documents" — the exact screen name — used to
        // drop the Documents entry, because `matches` only reads topic fields.
        const hitsModule = [m.name, m.location, m.whatItsFor].some((s) => s.toLowerCase().includes(q));
        const topics = m.topics.filter((t) => allows(viewer, t.access));
        // A name match keeps the whole entry; otherwise keep the matching topics.
        return { ...m, topics: hitsModule ? topics : topics.filter((t) => matches(t, q)) };
      })
      .filter((m) => m.topics.length > 0);
  }, [viewer, q]);

  // Progress across everything THIS reader is shown — foundations, their open
  // track and the appendix. Counting the whole guide would be dishonest: a PM is
  // not incomplete for never reading the admin track they cannot see.
  const myTally = useMemo(() => {
    // Shared with the admin roll-up so the two can't disagree — and so the
    // appendix is counted, which both had missed.
    const ids = targetTopics(viewer, active?.id);
    let done = 0;
    for (const id of ids) if (progressBy.get(id)?.completedAt != null) done++;
    return { done, total: ids.size };
  }, [viewer, active, progressBy]);

  // ── Mode branch. Both alternatives are whole screens rather than variations
  //    on the page, so they return early instead of threading conditionals
  //    through 130 lines of markup below.
  if (entry === undefined) return <div className="tg-wrap" />;

  if (entry === 'start') {
    return (
      <StartHere
        viewer={reader}
        tracks={tracks}
        suggested={suggested}
        onPick={pickRole}
        onBrowse={() => pickMode('reference')}
      />
    );
  }

  if (entry === 'guided') {
    return (
      <GuidedMode
        viewer={reader}
        trackId={active?.id}
        progressBy={progressBy}
        startAt={guidedAt}
        onExit={() => pickMode('reference')}
        onChangeRole={backToStart}
      />
    );
  }

  const shownFoundations = filtered(foundations);
  const shownTrack = filtered(trackSections);
  // Must count the appendix too, or a query matching only a module topic (say
  // "Superseding") shows "nothing matches" directly above the card that matched.
  const nothingFound = q.length > 0
    && shownFoundations.length === 0 && shownTrack.length === 0 && shownModules.length === 0;

  return (
    <div className="tg-wrap">
      <style>{css}</style>

      <div className="tg-head">
        <h1>{TRAINING.title}</h1>
        <p className="tg-sub">
          Revision {TRAINING.revision} · reviewed {TRAINING.revised} · this guide only shows what your
          access lets you reach, so it may be shorter than a colleague’s.
        </p>
      </div>

      {isFieldLeaderOnly(reader) ? (
        <div className="tg-scope" style={{ marginTop: 'var(--ew-s-4)' }}>
          Your access is scoped to the jobs you’re assigned to, so the company-wide sections
          (Manpower, Equipment, QA/QC roll-ups) aren’t shown here — they aren’t part of your view
          of the tracker.
        </div>
      ) : active?.id === 'field-leader' ? (
        <div className="tg-scope" style={{ marginTop: 'var(--ew-s-4)' }}>
          Showing the <b>field leader’s</b> view. Field leaders only see the jobs they’re assigned
          to, so this track deliberately leaves out every company-wide screen — this is what they
          will actually read.
        </div>
      ) : null}

      <div className="tg-bar">
        {tracks.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`tg-pill${active?.id === t.id ? ' active' : ''}`}
            onClick={() => setTrack(t.id)}
            title={t.audience}
          >
            {t.label}
            {suggested?.id === t.id && <span className="tg-you">you</span>}
          </button>
        ))}
        <input
          className="tg-search"
          placeholder="Search the guide…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search the training guide"
        />
        {myTally.total > 0 && (
          <span
            className="tp-progress"
            title="The foundations plus the track you have open, counting only what your access lets you see. The screen-by-screen appendix is reference and is not counted."
          >
            {myTally.done} of {myTally.total} done
          </span>
        )}
        {/* The way back into the walkthrough. It picks up at the first topic the
            reader hasn't ticked, so this is "carry on" as much as "start". */}
        <button
          type="button"
          className="tg-pill"
          title="Go through the guide one topic at a time, picking up where you left off"
          onClick={() => pickMode('guided')}
        >
          ▶ Walk me through it
        </button>
        {/* Same content, same access filtering — see lib/docx. The label says
            "your copy" because it is genuinely per-reader, not the same file
            for everyone. */}
        <button
          type="button"
          className="tg-pill"
          disabled={exporting}
          title="Download this guide as a Word document, filtered to what you can see"
          onClick={async () => {
            setExporting(true);
            try {
              await downloadTrainingDocx(TRAINING, viewer, active?.label ?? 'Project Tracker user');
            } finally {
              setExporting(false);
            }
          }}
        >
          {exporting ? 'Preparing…' : '⤓ Word copy'}
        </button>
      </div>

      {nothingFound && (
        <div className="tg-empty">
          Nothing in your version of the guide matches “{q}”. It may belong to an area your access
          doesn’t cover — try a colleague with wider access, or ask in the Project Tracker feedback form.
        </div>
      )}

      {shownFoundations.length > 0 && (
        <>
          <div className="tg-part">
            <h2 className="tg-part-h">Everyone starts here</h2>
            {shownFoundations.map((s) => (
              <div className="tg-sec" key={s.id}>
                <h3 className="tg-sec-h">{s.title}</h3>
                {s.intro && <p className="tg-sec-intro">{s.intro}</p>}
                {s.topics.map((t) => <TopicCard key={t.id} topic={t} highlight={t.id === focusTopic} progress={progressBy.get(t.id)} />)}
              </div>
            ))}
          </div>
        </>
      )}

      {active && shownTrack.length > 0 && (
        <>
          <div className="tg-part">
            <h2 className="tg-part-h">{active.label}</h2>
            <p className="tg-sec-intro">{active.audience}</p>
            {shownTrack.map((s) => (
              <div className="tg-sec" key={s.id}>
                <h3 className="tg-sec-h">{s.title}</h3>
                {s.intro && <p className="tg-sec-intro">{s.intro}</p>}
                {s.topics.map((t) => <TopicCard key={t.id} topic={t} highlight={t.id === focusTopic} progress={progressBy.get(t.id)} />)}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Module reference. A track answers "what do I do?"; this answers "what
          is this screen?" for someone who has landed on one. Access-filtered
          the same way, and last, because it's a reference you look things up in
          rather than something you read through. */}
      {shownModules.length > 0 && (
        <div className="tg-part">
          <h2 className="tg-part-h">{TRAINING.modulesTitle}</h2>
          <p className="tg-sec-intro">
            What each part of the Project Tracker is for, where to find it, and the ideas particular to it.
            How to actually carry the work out is in the role tracks above. This is reference to look things
            up in rather than a course to finish, so there is nothing to mark done here and none of it counts
            against your progress — but you can still leave yourself a note on any of it.
          </p>
          {shownModules.map((m) => (
            <div className="tg-sec" key={m.id}>
              <h3 className="tg-sec-h">{m.name}</h3>
              <p className="tg-mod-loc">{m.location}</p>
              <p className="tg-sec-intro">{m.whatItsFor}</p>
              {m.topics.map((t) => <TopicCard key={t.id} topic={t} highlight={t.id === focusTopic} progress={progressBy.get(t.id)} reference />)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
